'use client';

import { useState, useCallback, useRef } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { offlineDB, type OfflineVoter } from '@/lib/offline-db';

interface UseVoterSearchReturn {
  results: OfflineVoter[];
  isSearching: boolean;
  search: (query: string) => Promise<void>;
  clearResults: () => void;
}

export function useVoterSearch(): UseVoterSearchReturn {
  const [results, setResults] = useState<OfflineVoter[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const { token, user, isAuthenticated } = useAuthStore();
  const { isOnline } = useAppStore();

  const search = useCallback(async (query: string) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      setResults([]);
      return;
    }

    if (!isAuthenticated || !user?.boothId) {
      console.warn('Cannot search: not authenticated or no booth assigned');
      return;
    }

    setIsSearching(true);

    // Cancel any in-flight API request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();

    try {
      // Always try IndexedDB first (offline-first approach)
      const localResults = await offlineDB.searchVoter(trimmedQuery, user.boothId);

      if (localResults.length > 0) {
        setResults(localResults);
        setIsSearching(false);
        return;
      }

      // If no local results and we're online, try the API
      if (isOnline && token) {
        try {
          const response = await fetch(
            `/api/voters?search=${encodeURIComponent(trimmedQuery)}&boothId=${encodeURIComponent(user.boothId)}`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
              },
              signal: abortRef.current.signal,
            }
          );

          if (response.ok) {
            const data = await response.json();
            const apiResults: OfflineVoter[] = data.voters || data || [];

            if (apiResults.length > 0) {
              setResults(apiResults);
              // Cache results locally for future offline use
              await offlineDB.saveVoters(apiResults);
            } else {
              setResults([]);
            }
          } else {
            // API failed, keep empty results
            setResults([]);
          }
        } catch (error) {
          // AbortError is fine (cancelled request), others log
          if (error instanceof DOMException && error.name === 'AbortError') {
            return;
          }
          console.error('API voter search failed:', error);
          setResults([]);
        }
      } else {
        // Offline and no local results
        setResults([]);
      }
    } catch (error) {
      console.error('Voter search error:', error);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [isAuthenticated, user, token, isOnline]);

  const clearResults = useCallback(() => {
    setResults([]);
  }, []);

  return {
    results,
    isSearching,
    search,
    clearResults,
  };
}
